import React from 'react';
import { Link } from 'react-router-dom';
import { breakpoints } from '@/layouts/breakpoints'
import styled  from 'styled-components';
import AsciiCard from '@/components/landing/common/AsciiCard';

import {
  TrendingUp,
  PieChart,
  ArrowLeftRight,
  ScrollText,
  Globe,
  Bitcoin,
  FileText,
  FileSearch,
  Database,
  Newspaper,
  Leaf,
  Award,
  Share2,
  MessageCircle,
  Activity,
  Building2,
  LayoutGrid,
  Rocket,
  ChevronRight
} from 'lucide-react';


const StyledComp = styled.div`
  position: relative;
  z-index: 9;
  transition-delay: 0.5s;
  transition-duration: 0.3s;
  background-color: transparent;
  &.visible{
    transition-duration: 0.3s;
    background-color: white;
    transition-delay: 0s;
  }
  .text{
    p{
      margin: 0 auto;
      width: 440px;
      font-size: 12px;
      line-height: 1.4;
      color: var(--grey);

      @media (max-width: ${breakpoints.medium}) {
        width: 100%;
      }
    }
    h2{
     width: 100%;

    }
    padding: 40px;
    text-align: center;

    @media (max-width: ${breakpoints.medium}) {
      padding: 20px;
    }
  }
  .cards-container{
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    gap: 0px;
    padding: 20px 0px;
    h3{
      display: flex;
      flex-direction: row;
      align-items: center;

      svg{
        padding-right: 20px;
      }
    }

    .card{
      border-bottom: 1px solid rgba(var(--greyr), 0.3);
      border-right: 1px solid rgba(var(--greyr), 0.3);
      padding: 20px;
      width: 25%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      position: relative;
      overflow: hidden;

      .ccopy{
        position: relative;
        z-index:2;
        background-color: rgba(var(--whiter), 0.7);
        backdrop-filter: blur(2px);
        padding: 20px;
        border-radius: 15px;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      &:nth-child(3n){
        border-right: none;
      }

      &:nth-last-child(1), &:nth-last-child(2), &:nth-last-child(3){
        border-bottom: none;
      }


      @media (max-width: ${breakpoints.large}) {
        width: 33%;
        height: auto;
        min-height: 250px;
        border-right: 1px solid rgba(var(--greyr), 0.3);
        &:nth-child(3n){
          border-right: 1px solid rgba(var(--greyr), 0.3);
        }
        &:nth-last-child(3){
          border-bottom: 1px solid rgba(var(--greyr), 0.3);
        }

        &:nth-child(2n){
          border-right: none;
        }

        &:nth-last-child(1), &:nth-last-child(2){
          border-bottom: none;
        }


      }

      @media (max-width: ${breakpoints.medium}) {
        width: 100%;
        border-right: none !important;

        &:nth-last-child(2), &:nth-last-child(3) {
          border-bottom: 1px solid rgba(var(--greyr), 0.3);
        }

        &:last-child{
          border-bottom: none !important;
        }
      }


      h3{
        color: var(--grey);
        padding-top: 10px;
      }

      p{
        padding: 20px 0;
        font-size: 12px;
        line-height: 1.8;
        min-height: 100px;
      }

      .details{
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 10px 0;
        gap: 12px;
        flex-wrap: wrap;
        >div{
          display: flex;
          flex-direction: column;
        }

        b{
          font-size: 9px;
          color: var(--grey);
        }

        span{
          font-size: 18px;

          padding: 6px 0;
        }
      }

     .link{
        display: inline-flex;
        flex-direction: row;
        width: auto;
        align-items: center;
        padding: 12px;
        margin-top: 12px;
        font-size: 12px;
        transition-duration: 0.3s;
        border-radius: 8px;
        pointer-events: all;
        color: var(--grey);
        transform: translateX(-12px);
        align-self: flex-start;
          svg{
            margin-left: 4px;
            width: 12px;
            height: 12px;
            transition: all 0.3s;
          }
        }
      }
    }

    a{
       &:hover{
        .link{
            transform: translateX(0px);
            color: var(--black);
            svg{
              transition: all 0.3s;
              transform: translateX(2px);
            }
            transition-duration: 0.3s;
            background-color: #eee;
          }
        }
      }

  }
`;

const colorways = ['purple', 'blue', 'green', 'red', 'orange', 'teal', 'pink'];

const cards = [
  {
    icon: TrendingUp,
    title: 'STOCKS',
    colorway: colorways[0],
    desc: 'Global equity coverage with daily and historical stock data, deep fundamentals, corporate actions, and decades of clean pricing. Built for long-horizon research, factor models, and systematic strategies.',
    details: [
      { label: 'TICKERS', value: '100,000+' },
      { label: 'DATA POINTS', value: '400B+' },
      { label: 'MARKETS', value: '85+' },
    ],
    href: '/products/stocks',
    linkText: 'Explore stocks',
  },
  {
    icon: Database,
    title: 'MACROS',
    colorway: colorways[1],
    desc: 'Global macroeconomic indicators across growth, inflation, employment, rates, and policy. Clean time series with consistent revisions for serious macro and regime analysis.',
    details: [
      { label: 'INDICATORS', value: '500,000+' },
      { label: 'COUNTRIES', value: '195+' },
      { label: 'YEARS HISTORY', value: '60+' },
    ],
    href: '/products/economics',
    linkText: 'Explore macro economics',
  },
  {
    icon: Globe,
    title: 'INDICES',
    colorway: colorways[2],
    desc: 'Comprehensive index data across regions, sectors, and asset classes. Track benchmarks, create custom universes, and analyze macro trends with consistent historical depth.',
    details: [
      { label: 'INDICES', value: '150,000+' },
      { label: 'DATA POINTS', value: '50B+' },
      { label: 'MARKETS', value: '80+' },
    ],
    href: '/products/indices',
    linkText: 'Explore indices',
  },
  {
    icon: PieChart,
    title: 'ETFs',
    colorway: colorways[3],
    desc: 'Full ETF holdings, fund flows, and historical pricing. Understand exposure, track factor shifts, and analyze thematic strategies with full transparency.',
    details: [
      { label: 'ETFs', value: '10,000+' },
      { label: 'HOLDINGS', value: '5M+' },
      { label: 'MARKETS', value: '60+' },
    ],
    href: '/products/etfs',
    linkText: 'Explore ETFs',
  },
  {
    icon: ScrollText,
    title: 'FUTURES',
    colorway: colorways[4],
    desc: 'Global futures coverage across commodities, rates, equities, and energy. Continuous contracts, roll data, and long history designed for systematic and macro trading.',
    details: [
      { label: 'CONTRACTS', value: '50,000+' },
      { label: 'DATA POINTS', value: '100B+' },
      { label: 'EXCHANGES', value: '50+' },
    ],
    href: '/products/futures',
    linkText: 'Explore futures',
  },
  {
    icon: ArrowLeftRight,
    title: 'FOREX',
    colorway: colorways[5],
    desc: 'Institutional-quality FX data across major, minor, and exotic pairs. Reliable historical depth and daily pricing for macro analysis and algorithmic strategies.',
    details: [
      { label: 'PAIRS', value: '2,000+' },
      { label: 'DATA POINTS', value: '200B+' },
      { label: 'YEARS HISTORY', value: '30+' },
    ],
    href: '/products/forex',
    linkText: 'Explore forex',
  },
  {
    icon: Bitcoin,
    title: 'CRYPTO',
    colorway: colorways[0],
    desc: 'Spot and derivatives data across leading exchanges and assets. Built for research, volatility analysis, and long-term crypto market structure insights.',
    details: [
      { label: 'ASSETS', value: '10,000+' },
      { label: 'EXCHANGES', value: '100+' },
      { label: 'DATA POINTS', value: '50B+' },
    ],
    href: '/products/crypto',
    linkText: 'Explore crypto',
  },
  {
    icon: Award,
    title: 'CREDIT',
    colorway: colorways[1],
    desc: 'Credit ratings, spreads, and issuer-level data across corporate and sovereign markets. Analyze risk, capital structure, and credit cycles with clarity.',
    details: [
      { label: 'ISSUERS', value: '50,000+' },
      { label: 'INSTRUMENTS', value: '500,000+' },
      { label: 'MARKETS', value: '60+' },
    ],
    href: '/products/credit',
    linkText: 'Explore credit',
  },
  {
    icon: Leaf,
    title: 'ESG',
    colorway: colorways[2],
    desc: 'Standardized ESG scores, controversies, and historical changes over time. Move beyond static ratings and analyze sustainability as a measurable signal.',
    details: [
      { label: 'COMPANIES', value: '20,000+' },
      { label: 'DATA POINTS', value: '5B+' },
      { label: 'SCORE CATEGORIES', value: '300+' },
    ],
    href: '/products/esg',
    linkText: 'Explore ESG',
  },
  {
    icon: Share2,
    title: 'SUPPLY CHAIN',
    colorway: colorways[3],
    desc: 'Company-level supply chain data mapped across industries and geographies. Identify hidden dependencies, second-order risks, and structural advantages.',
    details: [
      { label: 'COMPANIES', value: '20,000+' },
      { label: 'RELATIONSHIPS', value: '1M+' },
      { label: 'INDUSTRIES', value: '150+' },
    ],
    href: '/products/supply-chain',
    linkText: 'Explore supply chain',
  },
  {
    icon: Newspaper,
    title: 'NEWS',
    colorway: colorways[4],
    desc: 'Daily and historical news coverage across global markets, companies, and macroeconomic events. Structured article data with entity tagging, news sentiment scores, and source metadata built for event-driven research and systematic news-based strategies.',
    details: [
      { label: 'ARTICLES', value: '10M+' },
      { label: 'SOURCES', value: '50,000+' },
      { label: 'LANGUAGES', value: '30+' },
    ],
    href: '/products/news',
    linkText: 'Explore news',
  },
  {
    icon: MessageCircle,
    title: 'ADVANCED SENTIMENT',
    colorway: colorways[5],
    desc: 'Quantified sentiment data from news, earnings calls, social signals, and web sources. Track narrative shifts, measure crowd behavior, and turn unstructured data into actionable signals.',
    details: [
      { label: 'SIGNALS', value: '100M+' },
      { label: 'SOURCES', value: '500,000+' },
      { label: 'ENTITIES TRACKED', value: '100,000+' },
    ],
    href: '/products/sentiment',
    linkText: 'Explore sentiment',
  },
  {
    icon: FileText,
    title: 'EARNINGS',
    colorway: colorways[6],
    desc: 'Comprehensive earnings data with transcripts, call summaries, and analyst estimates. Track financial performance, guidance changes, and market reactions across global markets.',
    details: [
      { label: 'COMPANIES', value: '50,000+' },
      { label: 'REPORTS', value: '10M+' },
      { label: 'YEARS HISTORY', value: '20+' },
    ],
    href: '/products/earnings',
    linkText: 'Explore earnings',
  },
  {
    icon: FileSearch,
    title: 'DISCLOSURES',
    colorway: colorways[1],
    desc: 'Standardized SEC filings and financial disclosures from global markets. Access structured financial statements, insider transactions, and corporate actions with consistent formatting.',
    details: [
      { label: 'FILINGS', value: '100M+' },
      { label: 'COMPANIES', value: '50,000+' },
      { label: 'REGULATORS', value: '100+' },
    ],
    href: '/products/disclosures',
    linkText: 'Explore disclosures',
  },
  {
    icon: Activity,
    title: 'WEB TRAFFIC',
    colorway: colorways[0],
    desc: 'Digital engagement metrics across millions of domains. Track web traffic, user engagement, and online presence as leading indicators for company performance.',
    details: [
      { label: 'DOMAINS', value: '50M+' },
      { label: 'DATA POINTS', value: '500B+' },
      { label: 'SOURCES', value: '10,000+' },
    ],
    href: '/products/webtraffic',
    linkText: 'Explore web traffic',
  },
  {
    icon: Building2,
    title: 'CORPORATE PROFILES',
    colorway: colorways[1],
    desc: 'Company fundamentals, business descriptions, industry classifications, and corporate structure data. Build comprehensive company profiles with standardized identifiers and cross-referenced metadata.',
    details: [
      { label: 'COMPANIES', value: '50,000+' },
      { label: 'DATA POINTS', value: '10B+' },
      { label: 'MARKETS', value: '80+' },
    ],
    href: '/products/corporate-profiles',
    linkText: 'Explore profiles',
  },
  {
    icon: LayoutGrid,
    title: 'SEE ALL',
    colorway: colorways[2],
    desc: 'Browse our complete catalog of 16+ financial datasets. From equities and macro to alternative data and sentiment, find the intelligence your strategy demands.',
    details: [],
    href: '/products',
    linkText: 'View all products',
  },
  {
    icon: Rocket,
    title: 'GET STARTED',
    colorway: colorways[3],
    desc: 'Ready to build? Access your dashboard to grab API keys, monitor usage, explore the playground, and start integrating daily and historical financial data into your workflow.',
    details: [],
    href: '/dashboard',
    linkText: 'Go to dashboard',
  },
]

const Comp = ({isVisible}) => {
  const cardRefs = React.useRef(cards.map(() => React.createRef()))
  return (
    <StyledComp className={isVisible ? 'visible' : ''}>

      <div className="text">
        <h2 className="serif"> A single financial data API for every dataset </h2>
        <p> Axion delivers deep, reliable market intelligence across every major asset class and alternative data category. From daily and historical stock market data to complex relationship data, each dataset is normalized, versioned, and built for serious analysis.</p>
      </div>

      <div className="cards-container">
        {cards.map((card, i) => {
          const Icon = card.icon
          return (
            <Link className="card" key={i} ref={cardRefs.current[i]} to={card.href}>
              <AsciiCard hoverRef={cardRefs.current[i]} colorway={card.colorway} />
              <div className="ccopy">
                <h3 className="sans"> <Icon color="var(--grey)"/> {card.title} </h3>
                <p> {card.desc} </p>
                <div className="details">
                  {card.details.map((d, j) => (
                    <div key={j}>
                      <b className="sans"> {d.label} </b>
                      <span> {d.value} </span>
                    </div>
                  ))}
                </div>
                <span className="link"> {card.linkText} <ChevronRight /></span>
              </div>
            </Link>
          )
        })}
      </div>
    </StyledComp>
  );
};

export default Comp;
